import { CalendarDays, ExternalLink, Share2 } from "lucide-react";
import { Badge } from "@/components/badge";
import {
  meetingNoteDisplayTitle,
  meetingNotePreview,
  type MeetingNoteData,
} from "./meeting-note-types";
import { formatLocalDate, formatTimestamp } from "../../../../shared/local-date";

interface MeetingNoteRowProps {
  note: MeetingNoteData;
  onSelect: (note: MeetingNoteData) => void;
}

export function MeetingNoteRow({ note, onSelect }: MeetingNoteRowProps) {
  const title = meetingNoteDisplayTitle(note);
  const preview = meetingNotePreview(note.bodyJson) || "No note text yet";

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(note)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(note);
        }
      }}
      className="flex w-full cursor-pointer items-start gap-3 border-b border-l-4 border-l-transparent border-sand-200 px-5 py-3 text-left transition-colors hover:bg-sand-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
    >
      <span className="w-[80px] shrink-0 pt-0.5 text-sm text-sand-500">
        {formatLocalDate(note.meetingDate)}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="min-w-0 truncate text-sm font-semibold text-foreground">
            {title}
          </span>
          {note.fathomUrl && (
            <a
              href={note.fathomUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              className="inline-flex items-center gap-1 rounded-full border border-sand-200 bg-sand-100 px-2 py-0.5 text-[11px] font-semibold text-sand-500 transition-colors hover:text-foreground"
            >
              <ExternalLink className="h-3 w-3" />
              Fathom
            </a>
          )}
          {note.lastSharedAt && (
            <Badge variant="category" className="gap-1 px-2 py-0.5 text-[11px]">
              <Share2 className="h-3 w-3" />
              Shared {formatTimestamp(note.lastSharedAt)}
            </Badge>
          )}
        </span>

        <span className="mt-0.5 block text-[13px] text-sand-500 line-clamp-1">
          {preview}
        </span>

        <span className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-sand-400">
          <span className="inline-flex items-center gap-1">
            <CalendarDays className="h-3.5 w-3.5" />
            Meeting {formatLocalDate(note.meetingDate, "shortWithYear")}
          </span>
          <span>Supervisor: {note.supervisorName || "Unassigned"}</span>
          <span>Author: {note.authorName || "Unknown"}</span>
        </span>
      </span>
      <span className="mt-0.5 shrink-0 rounded-full bg-sand-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-sand-400">
        View
      </span>
    </div>
  );
}
